/**
 * Session Info Section Component
 * Matches Flutter's blade_screen.dart Section 1: Session Info
 *
 * Fields:
 * - Row 1: Start Time | Stop Time (hidden if null) | Duration (estimated if active)
 * - Row 2+: Total Energy, Stop Reason, Account ID, Account Name, Start Source (flow without gaps)
 */

import { useMemo } from 'react';
import { SessionDetail } from '../../types/session-types';
import { MeterValueGridRow } from '../../types/meter-value-types';
import { formatTime, formatDuration } from '../../utils/date-utils';

interface SessionInfoSectionProps {
  detail: SessionDetail;
  meterValues?: MeterValueGridRow[];
}

export function SessionInfoSection({ detail, meterValues = [] }: SessionInfoSectionProps) {
  const isActive = !detail.stopTime;

  // Estimated duration: now - startTime (seconds) when no stop time
  const estimatedDurationSeconds = useMemo(() => {
    if (!isActive || !detail.startTime) return null;
    const start = new Date(detail.startTime).getTime();
    if (isNaN(start)) return null;
    return Math.floor((Date.now() - start) / 1000);
  }, [isActive, detail.startTime]);

  // Estimated total energy from meter values when session has no totalEnergy
  const estimatedEnergy = useMemo(() => {
    if (!isActive || (detail.totalEnergy != null && detail.totalEnergy > 0)) return null;
    const sum = meterValues.reduce((s, r) => s + (r.deltaEnergy ?? 0), 0);
    return sum > 0 ? sum : null;
  }, [isActive, detail.totalEnergy, meterValues]);

  const durationSeconds = isActive ? estimatedDurationSeconds : detail.durationInSeconds;
  const totalEnergy = (detail.totalEnergy != null && detail.totalEnergy > 0)
    ? detail.totalEnergy
    : estimatedEnergy;
  const energyIsEstimated = totalEnergy != null && (detail.totalEnergy == null || detail.totalEnergy === 0);

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-4">Session Info</h3>

      {/* Row 1: Start Time | Stop Time (hidden if active) | Duration | Start Source (active only, 3rd col) */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <InfoItem label="Start Time" value={formatTime(detail.startTime)} />
        {!isActive ? (
          <InfoItem label="Stop Time" value={formatTime(detail.stopTime)} />
        ) : (
          <div>
            <div className="text-xs text-gray-500">Duration</div>
            <div className="text-sm font-semibold text-gray-900">
              {formatDuration(durationSeconds)}
            </div>
            {durationSeconds != null && (
              <div className="text-xs text-gray-400 italic">estimated</div>
            )}
          </div>
        )}
        {!isActive ? (
          <div>
            <div className="text-xs text-gray-500">Duration</div>
            <div className="text-sm font-semibold text-gray-900">
              {formatDuration(durationSeconds)}
            </div>
          </div>
        ) : (
          detail.startSource ? (
            <InfoItem label="Start Source" value={detail.startSource} />
          ) : <div />
        )}
      </div>

      {/* Remaining fields — flex wrap so visible items fill space with no gaps */}
      <div className="flex flex-wrap gap-x-8 gap-y-4">
        <div>
          <div className="text-xs text-gray-500">Total Energy [kWh]</div>
          <div className="text-sm font-semibold text-gray-900">
            {totalEnergy != null ? totalEnergy.toFixed(3) : '--'}
          </div>
          {energyIsEstimated && totalEnergy != null && (
            <div className="text-xs text-gray-400 italic">estimated</div>
          )}
        </div>
        {detail.stopReason && (
          <InfoItem label="Stop Reason" value={detail.stopReason} />
        )}
        {detail.accountId !== null && detail.accountId !== undefined && (
          <InfoItem label="Account ID" value={detail.accountId.toString()} />
        )}
        {detail.accountName && (
          <InfoItem label="Account Name" value={detail.accountName} />
        )}
        {!isActive && detail.startSource && (
          <InfoItem label="Start Source" value={detail.startSource} />
        )}
      </div>
    </div>
  );
}

interface InfoItemProps {
  label: string;
  value: string;
}

function InfoItem({ label, value }: InfoItemProps) {
  return (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm font-semibold text-gray-900">{value}</div>
    </div>
  );
}
